import { useReducer } from 'react';
import {
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from 'store/selectors/commonSelector';
import { useAppSelector } from 'store/store';
import { SkuDetails } from '../analytics/types';
import { StyleDetails } from '../components/FulfillmentOptions/types';

type InitProps = {
  skus: SkuDetails[];
  selected: Record<string, string>;
  enabled: Record<string, string[]> | undefined;
  justClicked: string;
  resolved: SkuDetails[];
  initialEnabled: Record<string, string[]> | undefined;
  quickviewAutoSelectEnabled?: boolean;
  quickViewproductNotAvailableImprovements: boolean;
};

type UseAttributeEngineProps = {
  style: StyleDetails;
  skus: SkuDetails[];
};

const filterResolvedSku = (skus: SkuDetails[], selected: Record<string, string>) => {
  return skus.filter((sku: SkuDetails) => {
    let invalidAttributes = [];
    if (sku.definingAttributes) {
      // checking each defining attribute against selections
      invalidAttributes = sku.definingAttributes.filter((attr) => {
        const selectedValue = selected[attr.name];
        if (selectedValue && attr.value !== selectedValue) {
          return true;
        }
        return false;
      });
    }

    // if there is inventory and no defining attributes conflict this sku is valid
    return invalidAttributes.length <= 0;
  });
};

const hasValidAttributes = (sku: SkuDetails, newSelected: Record<string, string>) => {
  if (!sku.definingAttributes) return false;

  return sku.definingAttributes.filter((attr) => {
    const selectedValue = newSelected[attr.name];
    return selectedValue && attr.value === selectedValue;
  });
};

const isSkuValidForMoreThanTwoAttributes = (
  sku: SkuDetails,
  validAttributes: Record<string, string>[],
  newSelected: Record<string, string>,
  justClicked: string,
) => {
  if (sku.definingAttributes.length > 2) {
    if (validAttributes.length >= Object.keys(newSelected).length - 1) {
      if (!justClicked && validAttributes.length === Object.keys(newSelected).length) {
        return true;
      }

      const justClickedAttribute = validAttributes.find((attr) => attr.name === justClicked);
      if (justClickedAttribute && justClickedAttribute.value === newSelected[justClicked]) {
        return true;
      }
    }
  }
  return false;
};

const isSkuValidForTwoOrLessAttributes = (validAttributes: any[]) => {
  return validAttributes.length > 0;
};

const filterPossibleEnabledSkus = (
  skus: SkuDetails[],
  newSelected: Record<string, string>,
  justClicked: string,
) => {
  return skus.filter((sku) => {
    if (newSelected && Object.keys(newSelected).length === 0) {
      return true;
    }

    const validAttributes = hasValidAttributes(sku, newSelected);

    if (sku.definingAttributes) {
      if (
        sku.definingAttributes.length > 2 &&
        sku.definingAttributes.length === Object.keys(newSelected).length
      ) {
        return validAttributes.length >= Object.keys(newSelected).length - 1;
      }

      if (isSkuValidForMoreThanTwoAttributes(sku, validAttributes, newSelected, justClicked)) {
        return true;
      }

      if (sku.definingAttributes.length <= 2 && isSkuValidForTwoOrLessAttributes(validAttributes)) {
        return true;
      }
    }

    return false;
  });
};

function init({
  skus,
  selected,
  enabled,
  justClicked,
  initialEnabled,
  quickviewAutoSelectEnabled,
  quickViewproductNotAvailableImprovements,
}: InitProps) {
  // Resole SKUs for selection(s)
  const newResolved = filterResolvedSku(skus, selected);

  // make auto selections based on a singular resolved sku
  let newSelected = {
    ...selected,
  };
  if (newResolved.length === 1 && quickviewAutoSelectEnabled) {
    if (newResolved[0].definingAttributes) {
      newResolved[0].definingAttributes.forEach((attr) => {
        newSelected = {
          ...newSelected,
          [attr.name]: attr.value,
        };
      });
    }
  }

  // Possible Enabled SKUs for selection(s) compared against newSelected to aid in determining proper enabled skus
  const possibleEnabled = filterPossibleEnabledSkus(skus, newSelected, justClicked);

  // enable all other values of recently selected attributes
  let newEnabled = {};

  // store enabled values using list of possible enabled skus
  possibleEnabled.forEach((sku) => {
    if (sku.definingAttributes) {
      sku.definingAttributes.forEach((attr) => {
        if (!newEnabled[attr.name] || newEnabled[attr.name].indexOf(attr.value) < 0) {
          const av = newEnabled[attr.name] || [];
          newEnabled = {
            ...newEnabled,
            [attr.name]: [...av, attr.value],
          };
        }
      });
    }
  });

  // set initialEnabled attributes when enabled is undefined
  let initEnabled;
  if (enabled === undefined) {
    // When enabled is undefined on the first render pass set initEnabled to the resolved newEnabled object
    initEnabled = {
      ...newEnabled,
    };
  } else {
    // Once enabled is defined we just want to maintain the initial state of initialEnabled
    initEnabled = {
      ...initialEnabled,
    };
  }

  if (
    enabled?.[justClicked] &&
    enabled?.[justClicked].indexOf(selected[justClicked]) > -1 &&
    Object.keys(selected).length === Object.keys(newSelected).length
  ) {
    // use previously enabled set for the attribute just selected
    // only use previous enabled set if the justClicked attribute exists in the enabled object and the selected value is in the enabled set list
    // the selected length must equal the newSelected length (handles cases where auto selections happen that could disable attributes)
    newEnabled = {
      ...newEnabled,
      [justClicked]: enabled[justClicked],
    };
  } else if (
    initialEnabled?.[justClicked] &&
    Object.keys(selected).length === 1 &&
    Object.keys(selected).length === Object.keys(newSelected).length
  ) {
    /*
      This is to solve a unique scenario when the justClicked value is not in the enabled list due to clicking a disabled option 
      this will set the justClicked attribute in newEnabled back to the initialEnabled state effectively resetting back to the initial quickview load enabled statuses
      The selected length must equal the newSelected length (handles cases where auto selections happen that could disable attributes)
    */
    newEnabled = {
      ...newEnabled,
      [justClicked]: initialEnabled[justClicked],
    };
  }

  // Update auto-selections based on newly available enabled options
  if (newEnabled && quickviewAutoSelectEnabled) {
    Object.keys(newEnabled).forEach((attribute) => {
      if (newEnabled[attribute].length === 1) {
        newSelected = {
          ...newSelected,
          [attribute]: newEnabled[attribute][0],
        };
      }
    });
  }

  return {
    skus,
    selected: newSelected,
    resolved: newResolved,
    enabled: newEnabled,
    initialEnabled: initEnabled,
  };
}

function reducer(state, action) {
  if (action.type === 'select') {
    const { attribute, value, quickViewproductNotAvailableImprovements } = action.payload;
    // If selection is already selected, do nothing
    if (state.selected && state.selected[attribute] === value) {
      return state;
    }

    const isEnabled = quickViewproductNotAvailableImprovements
      ? state.enabled[attribute]
      : state.enabled[attribute] && state.enabled[attribute].indexOf(value) > -1;

    let newSelected = null;
    // If it is enabled, add it to selections
    if (isEnabled) {
      newSelected = { ...state.selected, [attribute]: value };
      // If not enabled, clear existing selections and just use the desired one
    } else {
      newSelected = { [attribute]: value };
    }

    return init({
      skus: state.skus,
      selected: newSelected,
      resolved: state.resolved,
      enabled: state.enabled,
      justClicked: attribute,
      initialEnabled: state.initialEnabled,
      quickViewproductNotAvailableImprovements,
    });
  }

  return state;
}

function useAttributeEngine(props: UseAttributeEngineProps) {
  const { style, skus } = props;
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const quickviewAutoSelectEnabled = useAppSelector(
    getFeatureFlagByNameSelector('quickviewAutoSelectEnabled'),
  );
  const quickViewproductNotAvailableImprovements = useAppSelector(
    getFeatureFlagByNameSelector('QVproductNotAvailableImprovements'),
  );

  // Set up intial selections
  let selected = {};

  if (style) {
    // look for attributes with only one value and select them
    style.definingAttributes.forEach((attr) => {
      const values = attr.value.split('|');
      if (values.length === 1) {
        selected = { ...selected, [attr.name]: attr.value };
      }
    });
  }

  const [state, dispatch] = useReducer(
    reducer,
    {
      skus,
      selected,
      resolved: skus,
      storeIdentifierName,
      quickviewAutoSelectEnabled,
    },
    init,
  );

  function handleClick(attribute: string, value: string) {
    dispatch({
      type: 'select',
      payload: { attribute, value, quickViewproductNotAvailableImprovements },
    });
  }

  return [state, handleClick];
}

export default useAttributeEngine;
